import React, { useEffect, useState } from "react";
import "./Shop.css";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import { specSummary } from "../lib/listingFilters";
import Item from "../components/Item";
import PromotedAds from "../components/PromotedAds";
import NearbyListings from "../components/NearbyListings";
import FeaturedStores from "../components/FeaturedStores";
import Welcome from "../components/Welcome";
import ListingRail from "../components/ListingRail";
import { useAuth } from "../context/AuthContext";

// ─── Constants ────────────────────────────────────────────────────────────────
const CATEGORIES = [
  { icon: "📱", label: "Electronics",           slug: "electronics",            color: "#3b82f6" },
  { icon: "🚗", label: "Vehicles",              slug: "vehicles",               color: "#f97316" },
  { icon: "🏠", label: "Property",              slug: "property",               color: "#10b981" },
  { icon: "👗", label: "Fashion & Beauty",      slug: "fashion-beauty",         color: "#ec4899" },
  { icon: "💼", label: "Services",              slug: "services",               color: "#8b5cf6" },
  { icon: "👔", label: "Jobs",                  slug: "jobs",                   color: "#06b6d4" },
  { icon: "🎮", label: "Hobbies",               slug: "hobbies-entertainment",  color: "#f59e0b" },
  { icon: "🛋️", label: "Home & Furniture",     slug: "home-furniture",         color: "#84cc16" },
  { icon: "🌿", label: "Garden & Outdoor",      slug: "garden-outdoor",         color: "#22c55e" },
  { icon: "🚜", label: "Agriculture & Food",    slug: "agriculture-food",       color: "#a3e635" },
  { icon: "👶", label: "Baby & Kids",           slug: "baby-kids",              color: "#fb923c" },
  { icon: "🔌", label: "Gadgets",               slug: "gadgets-accessories",    color: "#818cf8" },
  { icon: "📦", label: "Misc & Others",         slug: "misc-others",            color: "#94a3b8" },
];

const TRUST_ITEMS = [
  { icon: "🛡️", title: "Escrow Protection",    body: "Your money is held safely until you confirm delivery." },
  { icon: "✅", title: "Verified Sellers",      body: "ID-verified sellers for extra peace of mind." },
  { icon: "💬", title: "In-App Messaging",      body: "Chat directly with buyers and sellers securely." },
  { icon: "⚡", title: "Instant Listings",      body: "Post your item in under 2 minutes, completely free." },
];

const HOW_STEPS = [
  { n: "1", icon: "📸", title: "Post your ad",       body: "Upload photos, add a title and price. It's 100% free." },
  { n: "2", icon: "💬", title: "Get offers",          body: "Buyers message you directly through TroveHub." },
  { n: "3", icon: "🤝", title: "Meet & close",        body: "Agree on a deal, use escrow for safe payment." },
  { n: "4", icon: "💸", title: "Get paid safely",     body: "Funds released to you once buyer confirms receipt." },
];

const STATS = [
  { value: "50K+", label: "Active Listings"  },
  { value: "20K+", label: "Happy Users"      },
  { value: "36",   label: "States Covered"   },
  { value: "100%", label: "Free to List"     },
];

// ─── Section: Hero ────────────────────────────────────────────────────────────
function HeroSection() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <section className="sh-hero">
      <div className="sh-hero-bg" />
      <div className="container sh-hero-inner">
        <div className="sh-hero-text">
          <div className="sh-hero-badge">
            <span className="sh-hero-badge-dot" />
            Nigeria's Safest Marketplace
          </div>
          <h1 className="sh-hero-title">
            Buy & Sell Safely<br />
            Across <span className="sh-hero-accent">Nigeria</span>
          </h1>
          <p className="sh-hero-sub">
            Direct deals and secure payments with TrovePay Escrow.<br />
            Electronics, vehicles, property, jobs and more.
          </p>

          <form className="sh-hero-form" onSubmit={submit}>
            <input
              type="text"
              placeholder="Search cars, phones, houses, solar..."
              value={q}
              onChange={e => setQ(e.target.value)}
            />
            <button type="submit">Search</button>
          </form>

          <div className="sh-hero-trending">
            <span>Popular:</span>
            {["iPhones", "Land", "Toyota Camry", "Solar Panels"].map(t => (
              <button key={t} onClick={() => navigate(`/search?q=${encodeURIComponent(t)}`)}>
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="sh-hero-cards">
          <div className="sh-hero-card sh-hero-card-1">
            <div className="sh-hc-icon">🛡️</div>
            <div className="sh-hc-body">
              <div className="sh-hc-title">TrovePay Escrow</div>
              <div className="sh-hc-sub">Pay safely, get refunded if needed</div>
            </div>
          </div>
          <div className="sh-hero-card sh-hero-card-2">
            <div className="sh-hc-icon">⚡</div>
            <div className="sh-hc-body">
              <div className="sh-hc-title">Post in 2 minutes</div>
              <div className="sh-hc-sub">Free listings, reach thousands</div>
            </div>
          </div>
          <div className="sh-hero-card sh-hero-card-3">
            <div className="sh-hc-icon">✅</div>
            <div className="sh-hc-body">
              <div className="sh-hc-title">Verified Sellers</div>
              <div className="sh-hc-sub">ID-checked for your safety</div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="sh-stats-bar">
        <div className="container sh-stats-inner">
          {STATS.map((s, i) => (
            <React.Fragment key={s.label}>
              <div className="sh-stat">
                <span className="sh-stat-val">{s.value}</span>
                <span className="sh-stat-lbl">{s.label}</span>
              </div>
              {i < STATS.length - 1 && <div className="sh-stat-div" />}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Section: Browse Categories ───────────────────────────────────────────────
// Category layout per screen size: how many fit across, and how many lines.
// Keep these numbers in sync with the .sh-cat-rail rules in Shop.css.
//   desktop 8 x 2 lines, tablet 6 x 2 lines, phone 3 x 3 lines.
// Categories beyond cols x lines are spread across the lines and scroll in sideways.
function useCatLayout() {
  const get = () => {
    const w = window.innerWidth;
    return w <= 768 ? "3x3" : w <= 1100 ? "6x2" : "8x2";
  };
  const [key, setKey] = useState(get);
  useEffect(() => {
    const on = () => setKey(get());
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  const [cols, lines] = key.split("x").map(Number);
  return { cols, lines };
}

function CategoriesSection() {
  const navigate = useNavigate();
  const { cols, lines } = useCatLayout();

  const first = CATEGORIES.slice(0, cols * lines);
  const extra = CATEGORIES.slice(cols * lines);
  const lineCount = Math.max(1, Math.ceil(first.length / cols));
  const rows = Array.from({ length: lineCount }, (_, r) => [
    ...first.slice(r * cols, (r + 1) * cols),
    ...extra.filter((_, i) => i % lineCount === r),
  ]);

  return (
    <section className="sh-section sh-categories-section">
      <div className="container">
        <div className="sh-section-head">
          <div>
            <h2 className="sh-section-title">Browse Categories</h2>
            <p className="sh-section-sub">Find exactly what you're looking for</p>
          </div>
          <Link to="/all" className="sh-see-all">See all listings →</Link>
        </div>

        <div className="sh-cat-rows">
          {rows.map((row, r) => (
            <div className="sh-cat-rail" key={r}>
              <ListingRail label={`Categories, row ${r + 1}`}>
                {row.map(c => (
                  <button
                    key={c.slug}
                    className="sh-cat-card"
                    style={{ "--cc": c.color }}
                    onClick={() => navigate(`/category/${c.slug}`)}
                  >
                    <div className="sh-cat-icon">{c.icon}</div>
                    <span>{c.label}</span>
                  </button>
                ))}
              </ListingRail>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// How many cards fit across (matches the CSS breakpoints): 4 desktop, 3 tablet, 2 phone
function useCols() {
  const get = () => (window.innerWidth <= 768 ? 2 : window.innerWidth <= 1100 ? 3 : 4);
  const [cols, setCols] = useState(get);
  useEffect(() => {
    const on = () => setCols(get());
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return cols;
}

// ─── Section: Listings grid (reusable) ───────────────────────────────────────
function ListingsSection({ title, sub, endpoint, viewAll }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const cols = useCols();

  useEffect(() => {
    api(endpoint)
      .then(d => setItems(Array.isArray(d) ? d : d.listings || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [endpoint]);

  const renderCard = (item, i) => (
    <Item
      key={item._id || item.id || i}
      _id={item._id}
      id={item.id || item._id}
      name={item.title}
      images={item.images}
      new_price={item.price}
      priceType={item.priceType}
      promoted={item.promoted}
      address={item.city || item.address}
      listing={item}
      trust={item.trust}
      specs={specSummary(item)}
    />
  );

  // The first 8 listings fill the same rows as the old grid; every row then scrolls sideways.
  // Extra listings (9+) are shared out across the rows and sit past the edge of the screen.
  const first = items.slice(0, 8);
  const extra = items.slice(8, 24);
  const rowCount = Math.max(1, Math.ceil(first.length / cols));
  const rows = Array.from({ length: rowCount }, (_, r) => [
    ...first.slice(r * cols, (r + 1) * cols),
    ...extra.filter((_, i) => i % rowCount === r),
  ]);

  return (
    <section className="sh-section">
      <div className="container">
        <div className="sh-section-head">
          <div>
            <h2 className="sh-section-title">{title}</h2>
            <p className="sh-section-sub">{sub}</p>
          </div>
          <Link to={viewAll} className="sh-see-all">View all →</Link>
        </div>

        {loading ? (
          <div className="sh-listings-skeleton">
            {Array(4).fill(0).map((_, i) => <div key={i} className="sh-skeleton-card" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="sh-listings-empty">No listings yet — be the first to post!</div>
        ) : (
          <div className="sh-rail-rows">
            {rows.map((row, r) => (
              <div className="sh-rail" key={r}>
                <ListingRail label={`${title}, row ${r + 1}`}>
                  {row.map(renderCard)}
                </ListingRail>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Section: Escrow / TrovePay promo banner ──────────────────────────────────
function EscrowBanner() {
  const { isLoggedIn } = useAuth();
  return (
    <section className="sh-escrow-banner">
      <div className="container sh-escrow-inner">
        <div className="sh-escrow-left">
          <div className="sh-escrow-badge">🔒 TrovePay Escrow</div>
          <h2>Trade with full confidence</h2>
          <p>
            Your payment is held safely by TroveHub until you receive and
            approve the item. Dispute raised? We'll mediate and refund if needed.
          </p>
          <div className="sh-escrow-steps">
            {[
              { icon: "💳", t: "Buyer pays"      },
              { icon: "📦", t: "Seller delivers" },
              { icon: "✅", t: "You confirm"     },
              { icon: "💸", t: "Seller paid"     },
            ].map((s, i, a) => (
              <React.Fragment key={s.t}>
                <div className="sh-escrow-step">
                  <span>{s.icon}</span>
                  <span>{s.t}</span>
                </div>
                {i < a.length - 1 && <span className="sh-escrow-arrow">→</span>}
              </React.Fragment>
            ))}
          </div>
          <Link
            to={isLoggedIn ? "/dashboard/escrow" : "/login"}
            className="sh-escrow-cta"
          >
            Learn more about TrovePay →
          </Link>
        </div>
        <div className="sh-escrow-visual">
          <div className="sh-escrow-card">
            <div className="sh-ec-row">
              <span className="sh-ec-icon">🛡️</span>
              <div>
                <div className="sh-ec-title">Payment Protected</div>
                <div className="sh-ec-sub">Funds held in escrow</div>
              </div>
              <span className="sh-ec-badge">Active</span>
            </div>
            <div className="sh-ec-divider" />
            <div className="sh-ec-row sh-ec-amount">
              <span>Amount held</span>
              <strong>₦250,000</strong>
            </div>
            <div className="sh-ec-row">
              <span>Status</span>
              <span className="sh-ec-status">Awaiting delivery</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Section: How it works ────────────────────────────────────────────────────
function HowItWorks() {
  return (
    <section className="sh-section sh-hiw-section">
      <div className="container">
        <div className="sh-section-head sh-section-head-center">
          <h2 className="sh-section-title">How TroveHub works</h2>
          <p className="sh-section-sub">List and sell in 4 simple steps</p>
        </div>
        <div className="sh-hiw-grid">
          {HOW_STEPS.map((s, i) => (
            <div key={s.n} className="sh-hiw-card">
              <div className="sh-hiw-num">{s.n}</div>
              <div className="sh-hiw-icon">{s.icon}</div>
              <h3>{s.title}</h3>
              <p>{s.body}</p>
              {i < HOW_STEPS.length - 1 && <div className="sh-hiw-arrow">→</div>}
            </div>
          ))}
        </div>
        <div className="sh-hiw-cta">
          <Link to="/dashboard/post" className="sh-cta-btn-primary">
            Post a Free Ad
          </Link>
          <Link to="/all" className="sh-cta-btn-ghost">
            Browse Listings
          </Link>
        </div>
      </div>
    </section>
  );
}

// ─── Section: Trust & Safety ──────────────────────────────────────────────────
function TrustSection() {
  return (
    <section className="sh-section sh-trust-section">
      <div className="container">
        <div className="sh-section-head sh-section-head-center">
          <h2 className="sh-section-title">Why choose TroveHub?</h2>
          <p className="sh-section-sub">Built for safe trading across Nigeria</p>
        </div>
        <div className="sh-trust-grid">
          {TRUST_ITEMS.map(t => (
            <div key={t.title} className="sh-trust-card">
              <div className="sh-trust-icon">{t.icon}</div>
              <h3>{t.title}</h3>
              <p>{t.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Main Shop page ───────────────────────────────────────────────────────────
// Order = what a shopper wants first: find things → see what's local & popular → trust & how it works.
export default function Shop() {
  return (
    <div className="shop-page">
      <HeroSection />

      {/* Shown on phones only (hero is hidden there) */}
      <Welcome />

      <CategoriesSection />

      <PromotedAds />

      <ListingsSection
        title="Popular Listings"
        sub="The most viewed items right now"
        endpoint="/popular"
        viewAll="/all"
      />

      <FeaturedStores />

      <EscrowBanner />

      <HowItWorks />

      <NearbyListings />

      <TrustSection />
    </div>
  );
}